import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SetStateAction,
} from "react";
import { createPortal } from "react-dom";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useTheme } from "@/hooks/useTheme";
import { Link, Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BuilderWordmark } from "@/components/BuilderWordmark";
import { getFeatures } from "@/config/features";
import { usePlan } from "@/contexts/PlanContext";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { generateOfferIfAllowed } from "@/lib/freeOfferMonthlyGate";
import { ensureDraftProjectAssignment } from "@/lib/projectAutoCreate";
import { fetchActiveSubscriptionForUser } from "@/lib/subscriptions";
import { createOfferShare, type OfferSharePayload } from "@/lib/offerShares";
import { MvpEstimateTable, type AddRowTemplateOption } from "@/components/mvp/MvpEstimateTable";
import { MvpStep3ClientConditions } from "@/components/mvp/MvpStep3ClientConditions";
import { normalizeStep3SectionIncludes } from "@/components/mvp/step3OfferTerms";
import { MvpWizard, type WizardNavActions } from "@/components/mvp/MvpWizard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatCompanyHeaderLine, loadCompanyPrefs } from "@/data/companyPrefs";
import {
  type MvpBundle,
  type MvpWorkshopStep,
  formatMvpDraftLabel,
  freshMvpBundle,
  getMvpDraftById,
  hasMvpDraftProgress,
  inferMvpWorkshopStepFromDraft,
  useMvpPersistence,
} from "@/hooks/use-mvp-persistence";
import {
  WORK_TYPE_LABELS,
  isEstimateLineRow,
  isSectionHeader,
  parseWorkTypes,
  type ClientOfferTextOverrides,
  type EstimateLine,
  type FinishStandard,
  type MaterialsOwnership,
  type OfferRow,
  type RenovationType,
  type Step3OfferExtras,
  type VatMode,
  type WizardInput,
} from "@/types/mvpEstimate";
import { DEFAULT_TEMPLATE_ID, type OfferTemplateId } from "@/types/offerTemplates";
import type { OfferData, TemplateType } from "@/components/offer/offerDocumentTypes";
import {
  emptyEstimateLine,
  generateEstimateLinesWithTemplates,
} from "@/utils/mvpEstimateGenerator";
import { WIZARD_REGION_OPTIONS } from "@/wizard/mvpWizardScopeCatalog";
import {
  applyGlobalVatRateToLines,
  effectiveLineVatRate,
  estimateVatSummary,
  lineGrossPrice,
  roundMoney,
  vatRateFromMode,
} from "@/utils/mvpEstimateMath";
import { ClientOfferView } from "@/components/ClientOfferView";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { OfferPreviewWithTemplateSidebar } from "@/components/OfferPreviewWithTemplateSidebar";
import { OfferDocumentModeCards } from "@/components/mvp/OfferDocumentModeCards";
import { OFFER_DOCUMENT_MODE_OPTIONS } from "@/components/mvp/offerDocumentModeOptions";
import { buildOfferNumber } from "@/components/offer/offerDocumentUtils";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Loader2,
  LogOut,
  Moon,
  Settings,
  Sun,
  User2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/posthog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { estimateLineElementId } from "@/utils/estimateReviewSummary";
const GENERATE_DELAY_MS = 500;

const STEP3_MATERIALS_REQUIRED_MSG =
  "Wybierz, kto dostarcza materiały, aby przejść dalej.";

const OFFER_TEXT_DEFAULTS: Record<
  "title" | "includesIntro" | "timelineNote" | "priceDisclaimer" | "closingLead" | "closingSecond",
  string
> = {
  title: "Oferta remontowa",
  includesIntro: "Oferta obejmuje kompleksowy zakres prac niezbędnych do realizacji remontu, w tym:",
  timelineNote:
    "Szacowany czas realizacji: orientacyjnie 2–4 tygodnie — do potwierdzenia po uzgodnieniu zakresu i terminów.",
  priceDisclaimer: "Ceny obowiązują w dniu wystawienia oferty.",
  closingLead: "W razie pytań lub chęci dopasowania zakresu — zapraszam do kontaktu.",
  closingSecond: "Zakres i ceny można dostosować do Państwa potrzeb.",
};

function applyOfferTextsPatch(
  prev: ClientOfferTextOverrides | undefined,
  patch: Partial<ClientOfferTextOverrides>
): ClientOfferTextOverrides | undefined {
  const next: Record<string, string | undefined> = { ...(prev ?? {}) };

  (Object.keys(patch) as Array<keyof ClientOfferTextOverrides>).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(patch, key)) return;
    const raw = patch[key];

    if (key === "tagline") {
      if (raw === undefined) {
        delete next.tagline;
        return;
      }
      const trimmed = typeof raw === "string" ? raw.trim() : "";
      if (!trimmed) delete next.tagline;
      else next.tagline = raw;
      return;
    }

    if (!(key in OFFER_TEXT_DEFAULTS)) return;

    if (raw === undefined) {
      delete next[key as string];
      return;
    }

    const trimmed = typeof raw === "string" ? raw.trim() : "";
    const def = OFFER_TEXT_DEFAULTS[key as keyof typeof OFFER_TEXT_DEFAULTS];
    if (!trimmed || trimmed === def) {
      delete next[key as string];
    } else {
      next[key as string] = raw;
    }
  });

  const hasAny = Object.values(next).some((v) => typeof v === "string" && v.trim());

  return hasAny ? (next as ClientOfferTextOverrides) : undefined;
}

/** Pierwszy dzień następnego miesiąca po polsku, np. „1 maja”. */
function formatPolishRenewalFirstOfNextMonth(): string {
  const now = new Date();
  const firstNext = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return firstNext.toLocaleDateString("pl-PL", { day: "numeric", month: "long" });
}

/** Początek miesiąca w UTC (ISO) — zbliżone do `date_trunc('month', now())` po stronie Supabase. */
function utcMonthStartIso(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString();
}

/** Klucz miesiąca UTC „YYYY-MM” — spójny z limitem ofert (UTC). */
function currentUtcYm(): string {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
}

function isUuidString(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s.trim()
  );
}

/** Idempotency `requestId` trzymany w `drafts.data` (poza polami parsowanymi w `parseBundle`). */
type OfferGenRequestIdempotency = { monthYm: string; requestId: string };

function parseOfferGenIdemFromDraftData(data: unknown): OfferGenRequestIdempotency | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const raw = (data as Record<string, unknown>).offerGenRequestIdempotency;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const monthYm = (raw as Record<string, unknown>).monthYm;
  const requestId = (raw as Record<string, unknown>).requestId;
  if (typeof monthYm !== "string" || !/^\d{4}-\d{2}$/.test(monthYm)) return null;
  if (typeof requestId !== "string" || !isUuidString(requestId)) return null;
  return { monthYm, requestId: requestId.trim() };
}

function offerGenIdemLocalStorageKey(draftId: string, monthYm: string): string {
  return `builder-ai:offer-gen-req:${draftId}:${monthYm}`;
}

type FreeOfferGenPillModel =
  | { kind: "plain"; text: string; className: string }
  | { kind: "unlockLink"; lead: string; className: string };

/** Pill przy „Generuj ofertę” wg `remaining` (3 − użyte w miesiącu). Przy 0 — brak badge (blokada w overlay). */
function freeOfferGenPillFromMonthlyCount(usedRaw: number): FreeOfferGenPillModel | null {
  const used = Math.max(0, Math.min(3, Math.floor(usedRaw)));
  const remaining = 3 - used;
  if (remaining === 3) {
    return {
      kind: "plain",
      text: "3 bezpłatne oferty w tym miesiącu",
      className: "border-emerald-500/25 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100",
    };
  }
  if (remaining === 2) {
    return {
      kind: "plain",
      text: "Pozostały 2 bezpłatne oferty",
      className: "border-border/60 bg-muted/60 text-foreground/90",
    };
  }
  if (remaining === 1) {
    return {
      kind: "unlockLink",
      lead: "Pozostała 1 oferta · ",
      className:
        "border-amber-500/35 bg-amber-500/10 text-[color:var(--color-accent,#b45309)] dark:text-amber-100",
    };
  }
  return null;
}

const WORKSHOP_STEPS = ["Zakres prac", "Kosztorys", "Klient i warunki"] as const;
const WORKSHOP_SCOPE_SUBSTEPS = ["Typ prac", "Metraż", "Zakres per pokój"] as const;

function resolveWorkshopStepIndex(
  showEstimate: boolean,
  estimateStage: 2 | 3
): number {
  if (!showEstimate) return 1;
  return estimateStage;
}

function resolveCurrentStepFromUi(
  showEstimate: boolean,
  estimateStage: 2 | 3
): MvpWorkshopStep {
  if (!showEstimate) return 1;
  return estimateStage;
}

function applyMvpWorkshopStep(
  step: MvpWorkshopStep | 4,
  setters: {
    setShowEstimate: (v: boolean) => void;
    setEstimateStage: (v: 2 | 3) => void;
  }
): void {
  if (step === 1) {
    setters.setShowEstimate(false);
    return;
  }
  setters.setShowEstimate(true);
  setters.setEstimateStage(step === 2 ? 2 : 3);
}

function offerPreviewStorageKey(draftId: string): string {
  return `builder-mvp-offer-preview:${draftId}`;
}

function readStoredOfferPreviewOpen(draftId: string | undefined): boolean {
  if (!draftId || typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(offerPreviewStorageKey(draftId)) === "1";
}

function formatSavedAgo(ts: number | null): string {
  if (ts == null) return "Jeszcze nie zapisano";
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 8) return "Zapisano przed chwilą";
  if (sec < 60) return `Zapisano ${sec} s temu`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `Zapisano ${min} min temu`;
  return `Zapisano o ${new Date(ts).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`;
}

function userInitials(email: string | undefined): string {
  const e = (email ?? "").trim();
  if (!e) return "?";
  const local = e.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase() || "?";
}

export type MvpWizardChromeState = {
  draftDisplayName: string;
  draftShortId: string;
  workshopStepIndex: number;
  workshopProgressPct: number;
  lastSavedLabel: string;
  shellHidden: boolean;
};

export type MvpWizardPanelUi = {
  panelsCollapsible: boolean;
  sidebarCollapsed: boolean;
  previewCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setPreviewCollapsed: (collapsed: boolean) => void;
};

const defaultMvpWizardChrome: MvpWizardChromeState = {
  draftDisplayName: "Nowa oferta",
  draftShortId: "NOWY",
  workshopStepIndex: 1,
  workshopProgressPct: 25,
  lastSavedLabel: "Jeszcze nie zapisano",
  shellHidden: false,
};

const MvpWizardChromeContext = createContext<{
  setChrome: (patch: Partial<MvpWizardChromeState>) => void;
  panelUi: MvpWizardPanelUi | null;
  registerPanelUi: (ui: MvpWizardPanelUi | null) => void;
} | null>(null);

function useMvpWizardChrome() {
  return useContext(MvpWizardChromeContext);
}

/** Layout kreatora — sidebar tylko na trasie `/mvp/:draftId`. */
export function MvpWizardShellLayout() {
  const { draftId } = useParams();
  const { user } = useAuth();
  const [chrome, setChromeState] = useState<MvpWizardChromeState>(defaultMvpWizardChrome);
  const [panelUi, setPanelUi] = useState<MvpWizardPanelUi | null>(null);
  const setChrome = useCallback((patch: Partial<MvpWizardChromeState>) => {
    setChromeState((prev) => ({ ...prev, ...patch }));
  }, []);
  const registerPanelUi = useCallback((ui: MvpWizardPanelUi | null) => {
    setPanelUi(ui);
  }, []);

  const [shellSidebarOpen, setShellSidebarOpen] = useState(false);

  const sidebarOpen = panelUi != null ? !panelUi.sidebarCollapsed : shellSidebarOpen;

  const setSidebarOpen = useCallback(
    (open: boolean) => {
      if (panelUi) {
        panelUi.setSidebarCollapsed(!open);
        return;
      }
      setShellSidebarOpen(open);
    },
    [panelUi]
  );

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [sidebarOpen, setSidebarOpen]);

  if (!draftId) {
    return <Outlet />;
  }

  return (
    <MvpWizardChromeContext.Provider value={{ setChrome, panelUi, registerPanelUi }}>
      <div className={cn("workshop-modern workshop-shell", chrome.shellHidden && "hidden")}>
        <button
          type="button"
          className="workshop-sidebar-tab no-print"
          onClick={() => (sidebarOpen ? setSidebarOpen(false) : setSidebarOpen(true))}
          aria-expanded={sidebarOpen}
          aria-label={sidebarOpen ? "Zwiń menu nawigacji" : "Rozwiń menu nawigacji"}
        >
          {sidebarOpen ? "<" : ">"}
        </button>

        {sidebarOpen ? (
          <button
            type="button"
            className="workshop-sidebar-mask no-print"
            aria-label="Zamknij menu nawigacji"
            onClick={() => setSidebarOpen(false)}
          />
        ) : null}

        <aside
          className={cn("workshop-sidebar no-print", sidebarOpen && "is-open")}
          aria-expanded={sidebarOpen}
          aria-hidden={!sidebarOpen}
        >
          <div className="workshop-sidebar-body">
            <button
              type="button"
              className="workshop-panel-collapse-btn workshop-panel-collapse-btn--sidebar"
              onClick={() => setSidebarOpen(false)}
              aria-label="Zamknij menu nawigacji"
            >
              ✕
            </button>
            <Link to="/dashboard" className="workshop-sidebar-logo">
              <BuilderWordmark theme="dark" />
            </Link>
          <nav className="workshop-nav" aria-label="Główna nawigacja">
            <Link to="/dashboard">Start</Link>
            <Link to="/dashboard/oferty" className="is-active">
              Oferty
            </Link>
            <span className="is-disabled" title="Wkrótce">
              Klienci
            </span>
            <Link to="/moje-stawki">Pozycje</Link>
            <span className="is-disabled" title="Szablony ofert — wybierz w podglądzie kosztorysu">
              Szablony
            </span>
          </nav>
          <div className="workshop-draft-card">
            <h3>Wycena bieżąca</h3>
            <p className="workshop-draft-title">{chrome.draftDisplayName}</p>
            <div className="workshop-progress-track">
              <div
                className="workshop-progress-fill"
                style={{ width: `${chrome.workshopProgressPct}%` }}
              />
            </div>
            <p className="workshop-draft-meta">
              Krok {chrome.workshopStepIndex}/{WORKSHOP_STEPS.length} · {chrome.lastSavedLabel}
            </p>
          </div>
          <div className="workshop-user">
            <div className="workshop-user-avatar" aria-hidden>
              {userInitials(user?.email)}
            </div>
            <span className="workshop-user-name">{user?.email ?? "Konto"}</span>
          </div>
          </div>
        </aside>
        <div className="workshop-main">
          <Outlet />
        </div>
      </div>
    </MvpWizardChromeContext.Provider>
  );
}

const STANDARD_LABEL: Record<FinishStandard, string> = {
  ekonomiczny: "Ekonomiczny",
  standard: "Standard",
  premium: "Premium",
};

type RatesDatabase = Omit<Database, "public"> & {
  public: Omit<Database["public"], "Tables"> & {
    Tables: Database["public"]["Tables"] & {
      work_templates: {
        Row: {
          id: string;
          name: string;
          unit: string;
          category: string | null;
          default_labor_cost: number | null;
          default_material_cost: number | null;
          generator_key: string | null;
          is_custom: boolean;
        };
        Insert: {
          id?: string;
          name: string;
          unit: string;
          category?: string | null;
          default_labor_cost?: number | null;
          default_material_cost?: number | null;
          generator_key?: string | null;
          is_custom?: boolean;
        };
        Update: {
          id?: string;
          name?: string;
          unit?: string;
          category?: string | null;
          default_labor_cost?: number | null;
          default_material_cost?: number | null;
          generator_key?: string | null;
          is_custom?: boolean;
        };
        Relationships: [];
      };
      user_rates: {
        Row: {
          id: string;
          user_id: string;
          work_template_id: string;
          labor_cost_per_unit: number;
          material_cost_per_unit: number;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          work_template_id: string;
          labor_cost_per_unit?: number;
          material_cost_per_unit?: number;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          work_template_id?: string;
          labor_cost_per_unit?: number;
          material_cost_per_unit?: number;
          updated_at?: string;
        };
        Relationships: [];
      };
    };
  };
};

type UserRateRow = RatesDatabase["public"]["Tables"]["user_rates"]["Row"];
type WorkTemplateRow = RatesDatabase["public"]["Tables"]["work_templates"]["Row"];
type EstimateLineWithTemplate = EstimateLine & {
  work_template_id?: string | null;
  workTemplateId?: string | null;
  labor_cost_per_unit?: number;
  material_cost_per_unit?: number;
};

const supabaseWithRates = supabase as unknown as SupabaseClient<RatesDatabase>;

function getLineWorkTemplateId(line: EstimateLine): string | null {
  const candidate = line as EstimateLineWithTemplate;
  if (typeof candidate.work_template_id === "string" && candidate.work_template_id.trim()) {
    return candidate.work_template_id;
  }
  if (typeof candidate.workTemplateId === "string" && candidate.workTemplateId.trim()) {
    return candidate.workTemplateId;
  }
  return null;
}

export default function MvpEstimatePage() {
  const { user, signOut } = useAuth();
  const plan = usePlan();
  const features = getFeatures(plan);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { draftId } = useParams<{ draftId: string }>();
  const [invalidDraft, setInvalidDraft] = useState(false);
  // true once the draft has been loaded from Supabase (or confirmed missing)
  const [draftReady, setDraftReady] = useState(!draftId);
  const loadedDraftRef = useRef<{ draftId: string; userId: string } | null>(null);

  const [mvp, setMvp] = useState<MvpBundle>(freshMvpBundle);
  const mvpRef = useRef(mvp);
  mvpRef.current = mvp;
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreviewOverlay, setShowPreviewOverlay] = useState(false);
  /** Free tier: blokada po odrzuceniu przez RPC (nie przy otwarciu overlay). */
  const [offerGenBlockedView, setOfferGenBlockedView] = useState(false);
  /** Widoczny komunikat, gdy brama podglądu zwraca false (plan, limit, RPC itd.). */
  const [offerGenPreviewBlockReason, setOfferGenPreviewBlockReason] = useState<string | null>(
    null
  );
  const [offerGenSubmitting, setOfferGenSubmitting] = useState(false);
  const [offerGenRpcError, setOfferGenRpcError] = useState<string | null>(null);
  /** Idempotency dla RPC `generate_offer_if_allowed` — jeden `requestId` na szkic + miesiąc UTC (draft.data + localStorage). */
  const [offerGenRequestIdempotency, setOfferGenRequestIdempotency] =
    useState<OfferGenRequestIdempotency | null>(null);
  /** null = brak badge (ładowanie / błąd / PRO); tylko free tier po udanym count. */
  const [freeMonthlyOfferGenCount, setFreeMonthlyOfferGenCount] = useState<number | null>(null);
  /** Więcej miejsca na tabelę: węższa sekcja VAT, bez prawej kolumny i panelu Money Layer. */
  const [tableFocusMode, setTableFocusMode] = useState(false);
  /** Krok po wygenerowaniu kosztorysu: 2 = tabela, 3 = klient i warunki, 4 = szablon i wysyłka. */
  const [estimateStage, setEstimateStage] = useState<2 | 3>(2);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [previewCollapsed, setPreviewCollapsed] = useState(true);
  /** Pełna karta podsumowania VAT — zwinięty tylko pasek roboczy (sumy + stawka + narzucenie). */
  const [vatSummaryExpanded, setVatSummaryExpanded] = useState(false);
  const [recalcHint, setRecalcHint] = useState<string | null>(null);
  const recalcHintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shareCopyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tableWorkRegionRef = useRef<HTMLDivElement>(null);
  const prevShowEstimateRef = useRef<boolean | null>(null);
  const offerPreviewRestoredRef = useRef(false);

  useEffect(() => {
    offerPreviewRestoredRef.current = false;
  }, [draftId]);
  /** Synchroniczna blokada przed ustawieniem offerGenSubmitting (podwójny klik w tym samym cyklu). */
  const offerGenSubmitInFlightRef = useRef(false);
  const [shareState, setShareState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [wizardNav, setWizardNav] = useState<WizardNavActions | null>(null);
  /** Krok 3→4: blokada free tier przy ≥3 szkicach bez aktywnej subskrypcji. */
  const [step3DraftLimit, setStep3DraftLimit] = useState<{ used: number } | null>(null);
  const [step3ContinueChecking, setStep3ContinueChecking] = useState(false);
  const [step4ClientError, setStep4ClientError] = useState<string | null>(null);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [exitSaveInProgress, setExitSaveInProgress] = useState(false);
  const [templateFormatModalOpen, setTemplateFormatModalOpen] = useState(false);
  const [templateFormatModalIntent, setTemplateFormatModalIntent] =
    useState<"change" | "generate">("change");
  const [pendingTemplateType, setPendingTemplateType] = useState<TemplateType | null>(null);
  const [exitSaveError, setExitSaveError] = useState<string | null>(null);
  const [lastPersistedBundleSnapshot, setLastPersistedBundleSnapshot] = useState<string | null>(null);
  const { theme, toggleTheme } = useTheme();

  const [companyProfileOffer, setCompanyProfileOffer] = useState<{
    name: string;
    nip: string;
    address: string;
    phone: string;
    email: string;
    logoPath: string;
    logoUrl: string;
  } | null>(null);
  const [companyProfileLoading, setCompanyProfileLoading] = useState(false);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;
    let cancelled = false;
    setCompanyProfileLoading(true);
    const run = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("company_name, company_nip, company_address, company_phone, company_email, company_logo_url")
        .eq("id", userId)
        .single();
      if (cancelled) return;
      if (error) {
        setCompanyProfileOffer(null);
        setCompanyProfileLoading(false);
        return;
      }
      const profile = data as Partial<
        Record<
          | "company_name"
          | "company_nip"
          | "company_address"
          | "company_phone"
          | "company_email"
          | "company_logo_url",
          string | null
        >
      >;
      const company_name = profile.company_name ?? "";
      const company_nip = profile.company_nip ?? "";
      const company_address = profile.company_address ?? "";
      const company_phone = profile.company_phone ?? "";
      const company_email = profile.company_email ?? "";
      const company_logo_url = profile.company_logo_url ?? "";

      setCompanyProfileOffer({
        name: company_name,
        nip: company_nip,
        address: company_address,
        phone: company_phone,
        email: company_email,
        logoPath: typeof company_logo_url === "string" ? company_logo_url.trim() : "",
        logoUrl: "",
      });
      setCompanyProfileLoading(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Refresh signed URL when opening the real offer preview overlay.
  useEffect(() => {
    if (!showPreviewOverlay) return;
    const logoPath = companyProfileOffer?.logoPath?.trim();
    if (!logoPath) {
      setCompanyProfileOffer((prev) => (prev ? { ...prev, logoUrl: "" } : prev));
      return;
    }
    let cancelled = false;
    const run = async () => {
      const { data: signed } = await supabase.storage
        .from("company-logos")
        .createSignedUrl(logoPath, 60 * 60);
      if (cancelled) return;
      setCompanyProfileOffer((prev) => (prev ? { ...prev, logoUrl: signed?.signedUrl ?? "" } : prev));
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [showPreviewOverlay, companyProfileOffer?.logoPath]);

  useEffect(() => {
    const userId = user?.id;
    if (!draftId || !userId) {
      setDraftReady(true);
      return;
    }

    // Supabase potrafi emitować zdarzenia odświeżenia tokena po powrocie do karty,
    // co zmienia referencję `user` mimo tego samego `user.id`. Nie chcemy wtedy
    // "przeładowywać" widoku (ustawienie `draftReady=false` unmountuje UI i resetuje scroll).
    const alreadyLoaded =
      loadedDraftRef.current?.draftId === draftId && loadedDraftRef.current?.userId === userId;
    if (alreadyLoaded) return;

    setDraftReady(false);
    setOfferGenRequestIdempotency(null);
    void getMvpDraftById(draftId, userId).then(async (d) => {
      const ym = currentUtcYm();
      if (d) {
        setMvp(d.bundle);
        const navState =
          location.state != null && typeof location.state === "object"
            ? (location.state as {
                forceWorkshopStep1?: boolean;
                initialTemplateType?: TemplateType;
              })
            : null;
        const forceWorkshopStep1 = navState?.forceWorkshopStep1 === true;
        const initialTemplateType = navState?.initialTemplateType;
        if (
          initialTemplateType === "quick" ||
          initialTemplateType === "explained" ||
          initialTemplateType === "premium"
        ) {
          setMvp((s) => ({ ...s, templateType: initialTemplateType }));
        }
        if (forceWorkshopStep1) {
          applyMvpWorkshopStep(1, {
            setShowEstimate: (v) => setMvp((s) => ({ ...s, showEstimate: v })),
            setEstimateStage,
          });
        } else {
          const restoredStep = d.bundle.currentStep ?? inferMvpWorkshopStepFromDraft(d.bundle);
          applyMvpWorkshopStep(restoredStep, {
            setShowEstimate: (v) => setMvp((s) => ({ ...s, showEstimate: v })),
            setEstimateStage,
          });
        }
        setInvalidDraft(false);

        let resolved: OfferGenRequestIdempotency | null = null;
        const { data: row, error: rowErr } = await supabase
          .from("drafts")
          .select("data")
          .eq("id", draftId)
          .eq("user_id", userId)
          .single();
        if (!rowErr && row?.data != null) {
          const fromDraft = parseOfferGenIdemFromDraftData(row.data);
          if (fromDraft?.monthYm === ym) resolved = fromDraft;
        }
        if (!resolved && typeof window !== "undefined") {
          const ls = localStorage.getItem(offerGenIdemLocalStorageKey(draftId, ym));
          if (ls && isUuidString(ls)) resolved = { monthYm: ym, requestId: ls.trim() };
        }
        setOfferGenRequestIdempotency(resolved);
      } else {
        setInvalidDraft(true);
      }
      loadedDraftRef.current = { draftId, userId };
      setDraftReady(true);
    });
  }, [draftId, user?.id, location.state]);

  const persistOfferPreviewOpen = useCallback(() => {
    if (draftId && typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(offerPreviewStorageKey(draftId), "1");
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("preview", "true");
        return next;
      },
      { replace: true }
    );
  }, [draftId, setSearchParams]);

  const clearOfferPreviewPersistence = useCallback(() => {
    if (draftId && typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(offerPreviewStorageKey(draftId));
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("preview");
        return next;
      },
      { replace: true }
    );
  }, [draftId, setSearchParams]);

  useEffect(() => {
    if (!draftReady || invalidDraft || !draftId || offerPreviewRestoredRef.current) return;

    const fromUrl = searchParams.get("preview") === "true";
    const fromStorage = readStoredOfferPreviewOpen(draftId);
    if (!fromUrl && !fromStorage) return;

    offerPreviewRestoredRef.current = true;
    setShowPreviewOverlay(true);
    if (fromStorage && !fromUrl) {
      persistOfferPreviewOpen();
    } else if (fromUrl && !fromStorage && typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(offerPreviewStorageKey(draftId), "1");
    }
  }, [draftReady, invalidDraft, draftId, searchParams, persistOfferPreviewOpen]);

  const closePreviewOverlay = useCallback(() => {
    setShowPreviewOverlay(false);
    setOfferGenPreviewBlockReason(null);
    clearOfferPreviewPersistence();
  }, [clearOfferPreviewPersistence]);

  useEffect(() => {
    if (!showPreviewOverlay) {
      setOfferGenBlockedView(false);
      setOfferGenSubmitting(false);
      setOfferGenRpcError(null);
    }
  }, [showPreviewOverlay]);

  const refreshFreeMonthlyOfferGenCount = useCallback(async () => {
    if (!user?.id || plan.isLoading || plan.canUseProFeatures) {
      setFreeMonthlyOfferGenCount(null);
      return;
    }
    const from = utcMonthStartIso();
    const { count, error } = await supabase
      .from("offer_generations")
      .select("*", { count: "exact", head: true })
      .eq("user_id", user.id)
      .gte("created_at", from);
    if (error) {
      return;
    }
    setFreeMonthlyOfferGenCount(count ?? 0);
  }, [user?.id, plan.isLoading, plan.canUseProFeatures]);

  useEffect(() => {
    void refreshFreeMonthlyOfferGenCount();
  }, [refreshFreeMonthlyOfferGenCount, showPreviewOverlay]);

  useEffect(
    () => () => {
      if (shareCopyTimerRef.current) clearTimeout(shareCopyTimerRef.current);
    },
    []
  );

  const {
    wizard,
    lines: offerRows,
    showEstimate,
    vatMode,
    draftLabel,
    offerTexts,
    clientDetails,
    offerAssumptions,
    templateType = "quick",
    entryMode = "packages",
    templateId = DEFAULT_TEMPLATE_ID,
    materialsOwnership,
    materialsNotes,
    paymentTerms,
    warrantyType,
    exclusions,
    step3SectionIncludes,
    step3Extras,
  } = mvp;
  const step3Includes = useMemo(
    () => normalizeStep3SectionIncludes(step3SectionIncludes, templateType),
    [step3SectionIncludes, templateType]
  );
  const offerTextsForDocument = useMemo(() => {
    if (step3Includes.timeline || !offerTexts?.timelineNote) {
      return offerTexts;
    }
    const { timelineNote: _timelineNote, ...rest } = offerTexts;
    return rest;
  }, [offerTexts, step3Includes.timeline]);
  const lines = useMemo(() => offerRows.filter(isEstimateLineRow), [offerRows]);

  /** Ta sama logika co nagłówek oferty w `ClientOfferView` (profiles → fallback z preferencji). */
  const offerCompanyNameForHeader =
    companyProfileOffer?.name || formatCompanyHeaderLine(loadCompanyPrefs());
  const companyProfileReadyForOffer = !user?.id || !companyProfileLoading;
  const shouldWarnMissingCompanyName =
    companyProfileReadyForOffer && !String(offerCompanyNameForHeader ?? "").trim();

  const totalArea = wizard.rooms?.reduce((s, r) => s + r.areaM2, 0) ?? 0;

  useEffect(() => {
    if (!tableFocusMode || !showEstimate) return;
    const id = window.setTimeout(() => {
      tableWorkRegionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);
    return () => window.clearTimeout(id);
  }, [tableFocusMode, showEstimate]);

  const setWizard = (next: WizardInput) =>
    setMvp((s) => ({ ...s, wizard: next }));

  const setOfferRows = (next: SetStateAction<OfferRow[]>) =>
    setMvp((s) => {
      const linesNext = typeof next === "function" ? next(s.lines) : next;
      return { ...s, lines: linesNext };
    });

  const patchLineRows = useCallback(
    (mutator: (line: EstimateLine) => EstimateLine) => {
      setOfferRows((prev) => prev.map((row) => (isSectionHeader(row) ? row : mutator(row))));
    },
    [],
  );

  const setShowEstimate = (v: boolean) =>
    setMvp((s) => ({ ...s, showEstimate: v }));

  /** Docelowa stawka do narzucenia przyciskiem — nie zmienia linii przy samej zmianie listy. */
  const setVatMode = (next: VatMode) => setMvp((s) => ({ ...s, vatMode: next }));

  const handleTemplateChange = useCallback((nextTemplateId: OfferTemplateId) => {
    setMvp((s) => ({ ...s, templateId: nextTemplateId }));
  }, []);

  const handleDocumentLayoutTypeChange = useCallback((nextType: TemplateType) => {
    setMvp((s) => ({ ...s, templateType: nextType }));
  }, []);

  const openTemplateTypeSelector = useCallback(() => {
    setPendingTemplateType(templateType);
    setTemplateFormatModalIntent("change");
    setTemplateFormatModalOpen(true);
  }, [templateType]);

  const bundle = useMemo(
    () =>
      ({
        wizard,
        lines: offerRows,
        showEstimate,
        vatMode,
        currentStep: resolveCurrentStepFromUi(showEstimate, estimateStage),
        draftLabel,
        offerTexts,
        offerAssumptions,
        clientDetails,
        templateType,
        entryMode,
        templateId,
        materialsOwnership,
        materialsNotes,
        paymentTerms,
        warrantyType,
        exclusions,
        step3SectionIncludes,
        step3Extras,
        ...(offerGenRequestIdempotency
          ? { offerGenRequestIdempotency }
          : {}),
      }) as MvpBundle,
    [
      wizard,
      offerRows,
      showEstimate,
      estimateStage,
      vatMode,
      draftLabel,
      offerTexts,
      offerAssumptions,
      clientDetails,
      templateType,
      entryMode,
      templateId,
      materialsOwnership,
      materialsNotes,
      paymentTerms,
      warrantyType,
      exclusions,
      step3SectionIncludes,
      step3Extras,
      offerGenRequestIdempotency,
    ]
  );
  const allMainStepsUnlocked = useMemo(() => hasMvpDraftProgress(bundle), [bundle]);
  const { saveStatus, lastSavedAt } = useMvpPersistence(
    bundle,
    draftReady && !invalidDraft ? draftId : undefined
  );
  const bundleSnapshot = useMemo(() => JSON.stringify(bundle), [bundle]);

  useEffect(() => {
    setLastPersistedBundleSnapshot(null);
  }, [draftId]);

  useEffect(() => {
    if (!draftReady || invalidDraft) return;
    if (lastPersistedBundleSnapshot !== null) return;
    setLastPersistedBundleSnapshot(bundleSnapshot);
  }, [draftReady, invalidDraft, lastPersistedBundleSnapshot, bundleSnapshot]);

  useEffect(() => {
    if (saveStatus !== "saved") return;
    setLastPersistedBundleSnapshot(bundleSnapshot);
  }, [saveStatus, bundleSnapshot]);

  const hasUnsavedChanges =
    saveStatus === "saving" ||
    (lastPersistedBundleSnapshot !== null && bundleSnapshot !== lastPersistedBundleSnapshot);

  const isZeroValueLine = (l: EstimateLine) => {
    const totalCost = roundMoney(l.quantity * (l.laborUnitPrice + l.materialUnitPrice));
    return Math.abs(totalCost) < 0.005;
  };

  const payableLines = useMemo(
    () => lines.filter((l) => lineGrossPrice(l) > 0 && l.quantity > 0),
    [lines],
  );
  const incompleteLines = useMemo(() => lines.filter((l) => lineGrossPrice(l) === 0), [lines]);

  const vatSummary = useMemo(() => estimateVatSummary(payableLines), [payableLines]);

  /** Czy w pozycjach występuje więcej niż jedna stawka VAT (po zaokrągleniu). */
  const hasMultipleVatRates = useMemo(() => {
    const rates = new Set<number>();
    for (const l of lines) {
      // efektywna stawka bywa liczbą zmiennoprzecinkową; normalizujemy do 4 miejsc.
      const r = Math.round(effectiveLineVatRate(l) * 10_000) / 10_000;
      rates.add(r);
      if (rates.size > 1) return true;
    }
    return false;
  }, [lines]);

  /** Czy któraś pozycja ma inną stawkę niż wybrana dla oferty (np. stary zapis). */
  const linesVatMismatchOffer = useMemo(() => {
    const target = vatRateFromMode(vatMode);
    return lines.some((l) => Math.abs(effectiveLineVatRate(l) - target) > 1e-5);
  }, [lines, vatMode]);

  const applyVatToAllLines = () => {
    patchLineRows((line) =>
      applyGlobalVatRateToLines([line], vatMode)[0],
    );
  };

  const scrollToLine = (lineId: string) => {
    const el = document.getElementById(estimateLineElementId(lineId));
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    el?.classList.add("ring-2", "ring-primary/50", "ring-offset-2", "ring-offset-background");
    window.setTimeout(() => {
      el?.classList.remove("ring-2", "ring-primary/50", "ring-offset-2", "ring-offset-background");
    }, 2000);
  };

  const regionLabel = useMemo(
    () => WIZARD_REGION_OPTIONS.find((r) => r.id === wizard.region)?.label ?? wizard.region,
    [wizard.region]
  );

  const lineFingerprint = (rows: EstimateLine[]) =>
    JSON.stringify(
      rows.map((l) => ({
        n: l.name,
        q: l.quantity,
        lu: l.laborUnitPrice,
        mu: l.materialUnitPrice,
      }))
    );

  const applyUserRatesToGeneratedLines = useCallback(
    async (generatedLines: EstimateLine[]): Promise<EstimateLine[]> => {
      const userId = user?.id;
      if (!userId || generatedLines.length === 0) return generatedLines;

      const { data, error } = await supabaseWithRates
        .from("user_rates")
        .select("work_template_id, labor_cost_per_unit, material_cost_per_unit")
        .eq("user_id", userId);

      if (error || !data || data.length === 0) return generatedLines;

      const ratesByTemplateId = new Map<string, UserRateRow>(
        data.map((row) => [row.work_template_id, row]),
      );

      return generatedLines.map((line) => {
        const workTemplateId = getLineWorkTemplateId(line);
        if (!workTemplateId) return line;
        const rate = ratesByTemplateId.get(workTemplateId);
        if (!rate) return line;
        return {
          ...line,
          laborUnitPrice: Number(rate.labor_cost_per_unit),
          materialUnitPrice: Number(rate.material_cost_per_unit),
          labor_cost_per_unit: Number(rate.labor_cost_per_unit),
          material_cost_per_unit: Number(rate.material_cost_per_unit),
        };
      });
    },
    [user?.id],
  );

  const handleGenerate = () => {
    setIsGenerating(true);
    window.setTimeout(() => {
      const run = async () => {
        try {
          const generatedLines = await generateEstimateLinesWithTemplates(
            { ...wizard, renovationType: "mieszkanie" } as WizardInput & { renovationType?: RenovationType },
            vatRateFromMode(vatMode),
            {
              defaultLineMarginPercent: loadCompanyPrefs().defaultMarginPercent,
            },
          );
          const nextLines = await applyUserRatesToGeneratedLines(generatedLines);
          setMvp((s) => ({
            ...s,
            lines: nextLines,
            showEstimate: true,
          }));
          setEstimateStage(2);
          // Po wygenerowaniu: start od gory i od razu "tryb pracy".
          setTableFocusMode(true);
          window.setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
          }, 0);
        } finally {
          setIsGenerating(false);
        }
      };
      void run();
    }, GENERATE_DELAY_MS);
  };

  const handleRecalcFromWizard = () => {
    const prevLines = lines;
    const prevFp = lineFingerprint(prevLines);
    setIsGenerating(true);
    window.setTimeout(() => {
      const run = async () => {
        try {
          const generatedLines = await generateEstimateLinesWithTemplates(
            { ...wizard, renovationType: "mieszkanie" } as WizardInput & { renovationType?: RenovationType },
            vatRateFromMode(vatMode),
            {
              defaultLineMarginPercent: loadCompanyPrefs().defaultMarginPercent,
            },
          );
          const nextLines = await applyUserRatesToGeneratedLines(generatedLines);
          const nextFp = lineFingerprint(nextLines);
          setMvp((s) => ({ ...s, lines: nextLines }));
          if (prevFp === nextFp) {
            setRecalcHint("Zakres w kreatorze i takie same zalozenia - kosztorys bez zmian.");
          } else {
            setRecalcHint(`Zaktualizowano kosztorys: ${nextLines.length} pozycji wg aktualnego zakresu w kreatorze.`);
          }
          if (recalcHintTimer.current) clearTimeout(recalcHintTimer.current);
          recalcHintTimer.current = setTimeout(() => {
            setRecalcHint(null);
            recalcHintTimer.current = null;
          }, 4500);
        } finally {
          setIsGenerating(false);
        }
      };
      void run();
    }, GENERATE_DELAY_MS);
  };

  const handleAddRow = () => {
    setOfferRows((prev) => [
      ...prev,
      emptyEstimateLine(vatRateFromMode(vatMode), loadCompanyPrefs().defaultMarginPercent),
    ]);
  };

  const loadAddRowTemplates = useCallback(async (): Promise<AddRowTemplateOption[]> => {
    const userId = user?.id;
    if (!userId) return [];

    const [templatesRes, ratesRes] = await Promise.all([
      supabaseWithRates
        .from("work_templates")
        .select("id, name, unit, category, default_labor_cost, default_material_cost, generator_key, is_custom")
        .or("generator_key.not.is.null,is_custom.eq.true")
        .order("name", { ascending: true }),
      supabaseWithRates
        .from("user_rates")
        .select("work_template_id, labor_cost_per_unit, material_cost_per_unit")
        .eq("user_id", userId),
    ]);

    if (templatesRes.error) throw new Error(templatesRes.error.message);
    if (ratesRes.error) throw new Error(ratesRes.error.message);

    const templates = (templatesRes.data ?? []) as WorkTemplateRow[];
    const ratesByTemplateId = new Map<string, UserRateRow>(
      (ratesRes.data ?? []).map((row) => [row.work_template_id, row]),
    );

    return templates.map((template) => {
      const rate = ratesByTemplateId.get(template.id);
      const labor = rate ? Number(rate.labor_cost_per_unit) : Number(template.default_labor_cost ?? 0);
      const material = rate ? Number(rate.material_cost_per_unit) : Number(template.default_material_cost ?? 0);
      return {
        id: template.id,
        name: template.name,
        unit: template.unit,
        category: template.category ?? undefined,
        laborUnitPrice: labor,
        materialUnitPrice: material,
        source: rate ? "user_rate" : "default",
      };
    });
  }, [user?.id]);

  const handleRestorePricesFromTemplates = useCallback(async () => {
    setIsGenerating(true);
    try {
      const templates = await loadAddRowTemplates();
      const byId = new Map(templates.map((t) => [t.id, t]));
      let updatedCount = 0;
      setOfferRows((prev) => {
        updatedCount = prev.reduce((n, line) => {
          if (isSectionHeader(line)) return n;
          const wt = getLineWorkTemplateId(line);
          if (!wt) return n;
          return byId.get(wt) ? n + 1 : n;
        }, 0);
        return prev.map((line) => {
          if (isSectionHeader(line)) return line;
          const wt = getLineWorkTemplateId(line);
          if (!wt) return line;
          const tpl = byId.get(wt);
          if (!tpl) return line;
          const lab = roundMoney(tpl.laborUnitPrice);
          const mat = roundMoney(tpl.materialUnitPrice);
          const ext = line as EstimateLineWithTemplate;
          return {
            ...line,
            laborUnitPrice: lab,
            materialUnitPrice: mat,
            ...(ext.labor_cost_per_unit !== undefined || ext.material_cost_per_unit !== undefined
              ? { labor_cost_per_unit: lab, material_cost_per_unit: mat }
              : {}),
          };
        });
      });
      setRecalcHint(`Zaktualizowano ceny ${updatedCount} pozycji`);
      if (recalcHintTimer.current) clearTimeout(recalcHintTimer.current);
      recalcHintTimer.current = setTimeout(() => {
        setRecalcHint(null);
        recalcHintTimer.current = null;
      }, 4500);
    } finally {
      setIsGenerating(false);
    }
  }, [loadAddRowTemplates]);

  const handleRestoreRowPrice = useCallback(
    async (lineId: string) => {
      const templates = await loadAddRowTemplates();
      const byId = new Map(templates.map((t) => [t.id, t]));
      setOfferRows((prev) =>
        prev.map((line) => {
          if (isSectionHeader(line)) return line;
          if (line.id !== lineId) return line;
          const wt = getLineWorkTemplateId(line);
          if (!wt) return line;
          const tpl = byId.get(wt);
          if (!tpl) return line;
          const lab = roundMoney(tpl.laborUnitPrice);
          const mat = roundMoney(tpl.materialUnitPrice);
          const ext = line as EstimateLineWithTemplate;
          return {
            ...line,
            laborUnitPrice: lab,
            materialUnitPrice: mat,
            ...(ext.labor_cost_per_unit !== undefined || ext.material_cost_per_unit !== undefined
              ? { labor_cost_per_unit: lab, material_cost_per_unit: mat }
              : {}),
          };
        }),
      );
    },
    [loadAddRowTemplates],
  );

  const handleAddRowFromTemplate = useCallback(
    (template: AddRowTemplateOption) => {
      const nextLine: EstimateLineWithTemplate = {
        ...emptyEstimateLine(vatRateFromMode(vatMode), loadCompanyPrefs().defaultMarginPercent),
        name: template.name,
        unit: template.unit,
        quantity: 1,
        laborUnitPrice: roundMoney(template.laborUnitPrice),
        materialUnitPrice: roundMoney(template.materialUnitPrice),
        work_template_id: template.id,
        workTemplateId: template.id,
        labor_cost_per_unit: roundMoney(template.laborUnitPrice),
        material_cost_per_unit: roundMoney(template.materialUnitPrice),
      };
      setOfferRows((prev) => [...prev, nextLine]);
    },
    [vatMode],
  );

  const sharePaywall = !features.shareableLink;
  const shareBusy = features.shareableLink && user != null && shareState === "loading";
  const shareLinkUrl = shareToken ? `${window.location.origin}/oferta/${shareToken}` : null;

  const freeOfferGenPill = useMemo(() => {
    if (plan.isLoading || plan.canUseProFeatures || !user?.id) return null;
    if (freeMonthlyOfferGenCount === null) return null;
    return freeOfferGenPillFromMonthlyCount(freeMonthlyOfferGenCount);
  }, [plan.isLoading, plan.canUseProFeatures, user?.id, freeMonthlyOfferGenCount]);

  const handleCopyShareLink = async () => {
    if (!shareLinkUrl) return;
    try {
      await navigator.clipboard.writeText(shareLinkUrl);
      setShareCopied(true);
      if (shareCopyTimerRef.current) clearTimeout(shareCopyTimerRef.current);
      shareCopyTimerRef.current = setTimeout(() => {
        setShareCopied(false);
        shareCopyTimerRef.current = null;
      }, 2000);
    } catch {
      /* clipboard może być zablokowany — brak dodatkowego UI w MVP */
    }
  };

  const handleUdostepnijLink = useCallback(async (options?: { quiet?: boolean }) => {
    if (!features.shareableLink) return;
    if (!user) {
      navigate("/login", { state: { returnTo: window.location.pathname } });
      return;
    }
    if (lines.length === 0) return;
    if (!options?.quiet) {
      setShareState("loading");
      setShareError(null);
      setShareCopied(false);
    }

    const prefs = loadCompanyPrefs();
    const companyFromPrefs = formatCompanyHeaderLine(prefs);
    const ot = offerTexts as { companyName?: string } | undefined;
    const offerDateIso = new Date().toISOString();
    const offerNumber = buildOfferNumber(draftId, new Date(offerDateIso));
    const companyNameResolved = ot?.companyName?.trim()
      ? ot.companyName.trim()
      : companyProfileOffer?.name?.trim() || companyFromPrefs || undefined;

    const payload: OfferSharePayload = {
      schemaVersion: 1,
      createdAt: offerDateIso,
      wizard,
      lines: offerRows,
      vatMode,
      draftLabel: draftLabel ?? null,
      templateId,
      templateType,
      isPro: plan.canUseProFeatures,
      ...(offerTexts ? { offerTexts } : {}),
      ...(offerAssumptions && offerAssumptions.length > 0 ? { offerAssumptions } : {}),
      ...(materialsOwnership ? { materialsOwnership } : {}),
      ...(materialsNotes?.trim() ? { materialsNotes: materialsNotes.trim() } : {}),
      ...(paymentTerms?.trim() ? { paymentTerms: paymentTerms.trim() } : {}),
      ...(warrantyType?.trim() ? { warrantyType: warrantyType.trim() } : {}),
      ...(exclusions && exclusions.length > 0 ? { exclusions } : {}),
      clientOffer: {
        offerDate: offerDateIso,
        ...(draftId ? { draftId } : {}),
        ...(offerNumber ? { offerNumber } : {}),
        ...(clientDetails?.name?.trim() ? { clientName: clientDetails.name.trim() } : {}),
        ...(clientDetails?.address?.trim() ? { clientAddress: clientDetails.address.trim() } : {}),
        ...(clientDetails?.phone?.trim() ? { clientPhone: clientDetails.phone.trim() } : {}),
        ...(clientDetails?.email?.trim() ? { clientEmail: clientDetails.email.trim() } : {}),
        ...(companyNameResolved ? { companyName: companyNameResolved } : {}),
        ...(companyProfileOffer?.nip?.trim() ? { companyNip: companyProfileOffer.nip.trim() } : {}),
        ...(companyProfileOffer?.address?.trim() ? { companyAddress: companyProfileOffer.address.trim() } : {}),
        ...(companyProfileOffer?.phone?.trim() ? { companyPhone: companyProfileOffer.phone.trim() } : {}),
        ...(companyProfileOffer?.email?.trim() ? { companyEmail: companyProfileOffer.email.trim() } : {}),
        ...(companyProfileOffer?.logoPath?.trim() ? { companyLogoPath: companyProfileOffer.logoPath.trim() } : {}),
        ...(companyProfileOffer?.logoUrl?.trim() ? { companyLogoUrl: companyProfileOffer.logoUrl.trim() } : {}),
      },
    };

    const result = await createOfferShare(supabase, user.id, draftId ?? null, payload);
    if ("error" in result) {
      setShareState("error");
      setShareError(result.error);
      return;
    }
    setShareState("done");
    setShareToken(result.publicToken);
  }, [
    clientDetails?.address,
    clientDetails?.email,
    clientDetails?.name,
    clientDetails?.phone,
    companyProfileOffer?.address,
    companyProfileOffer?.email,
    companyProfileOffer?.logoPath,
    companyProfileOffer?.logoUrl,
    companyProfileOffer?.name,
    companyProfileOffer?.nip,
    companyProfileOffer?.phone,
    draftId,
    features.shareableLink,
    lines,
    offerRows,
    materialsNotes,
    materialsOwnership,
    navigate,
    offerAssumptions,
    offerTexts,
    paymentTerms,
    plan.canUseProFeatures,
    templateId,
    templateType,
    user,
    vatMode,
    exclusions,
    warrantyType,
    wizard,
  ]);

  /** Po utworzeniu linku — odśwież payload_json gdy wykonawca zmieni układ dokumentu (quick/explained/premium). */
  useEffect(() => {
    if (!shareToken || sharePaywall || !user || lines.length === 0) return;
    void handleUdostepnijLink({ quiet: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tylko templateType / templateId
  }, [templateType, templateId, shareToken]);

  useEffect(() => {
    const shouldPrepareShare =
      features.shareableLink &&
      user != null &&
      lines.length > 0 &&
      showPreviewOverlay;
    if (!shouldPrepareShare) return;
    if (sharePaywall || shareBusy || shareToken) return;
    void handleUdostepnijLink();
  }, [
    showPreviewOverlay,
    features.shareableLink,
    handleUdostepnijLink,
    lines.length,
    shareBusy,
    sharePaywall,
    shareToken,
    user,
  ]);

  type OfferPreviewGateResult = { ok: true } | { ok: false; reason: string };

  /** Free tier: jeden event w offer_generations — wyłącznie przy generowaniu oferty (krok 4). */
  const handleGenerateClientOfferPreview = async (): Promise<OfferPreviewGateResult> => {
    const nameOk = (clientDetails?.name ?? "").trim().length > 0;
    const addrOk = (clientDetails?.address ?? "").trim().length > 0;
    if (!nameOk || !addrOk) {
      return {
        ok: false,
        reason: "Uzupełnij dane adresata w kroku „Klient i warunki”, aby wygenerować ofertę.",
      };
    }
    if (step3Includes.materials && !materialsOwnership) {
      return {
        ok: false,
        reason: STEP3_MATERIALS_REQUIRED_MSG,
      };
    }

    setOfferGenRpcError(null);
    setOfferGenBlockedView(false);
    setOfferGenPreviewBlockReason(null);

    if (plan.isLoading) {
      return { ok: false, reason: "Trwa wczytywanie planu. Spróbuj ponownie za chwilę." };
    }

    if (plan.canUseProFeatures || !draftId || !user?.id) {
      return { ok: true };
    }

    if (offerGenSubmitInFlightRef.current || offerGenSubmitting) {
      return { ok: false, reason: "Generowanie oferty jest już w toku." };
    }

    offerGenSubmitInFlightRef.current = true;
    setOfferGenSubmitting(true);

    const ym = currentUtcYm();
    let requestId =
      offerGenRequestIdempotency?.monthYm === ym ? offerGenRequestIdempotency.requestId : null;
    if (!requestId && typeof window !== "undefined") {
      const ls = localStorage.getItem(offerGenIdemLocalStorageKey(draftId, ym));
      if (ls && isUuidString(ls)) requestId = ls.trim();
    }
    if (!requestId) {
      requestId = crypto.randomUUID();
    }
    const idem: OfferGenRequestIdempotency = { monthYm: ym, requestId };
    if (typeof window !== "undefined") {
      localStorage.setItem(offerGenIdemLocalStorageKey(draftId, ym), requestId);
    }
    setOfferGenRequestIdempotency(idem);

    try {
      const result = await generateOfferIfAllowed(supabase, draftId, requestId);

      if ("error" in result) {
        setOfferGenRpcError(result.error);
        return { ok: false, reason: result.error };
      }
      if (!result.allowed) {
        console.log("POSTHOG EVENT:", "limit_hit");
        trackEvent("limit_hit");
        setOfferGenBlockedView(true);
        return {
          ok: false,
          reason: "Wykorzystałeś limit 3 ofert w tym miesiącu (plan Free).",
        };
      }
      void refreshFreeMonthlyOfferGenCount();
      console.log("POSTHOG EVENT:", "quote_generated");
      trackEvent("quote_generated");
      return { ok: true };
    } finally {
      offerGenSubmitInFlightRef.current = false;
      setOfferGenSubmitting(false);
    }
  };

  const openPreviewOverlayAfterGate = useCallback(async () => {
    const gate = await handleGenerateClientOfferPreview();
    if (!gate.ok) {
      setOfferGenPreviewBlockReason(gate.reason);
      return;
    }

    const name = (clientDetails?.name ?? "").trim();
    const addr = (clientDetails?.address ?? "").trim();
    if (user?.id && draftId && name && addr) {
      void ensureDraftProjectAssignment({
        userId: user.id,
        draftId,
        clientName: name,
        clientAddress: addr,
        bundle: mvpRef.current,
      });
    }

    setOfferGenPreviewBlockReason(null);
    setShowPreviewOverlay(true);
    persistOfferPreviewOpen();
  }, [
    handleGenerateClientOfferPreview,
    persistOfferPreviewOpen,
    clientDetails?.name,
    clientDetails?.address,
    user?.id,
    draftId,
  ]);

  const step4OfferQuotaLabel = useMemo(() => {
    if (plan.isLoading || plan.canUseProFeatures || !user?.id) return null;
    if (freeMonthlyOfferGenCount === null) return null;
    const used = Math.max(0, Math.min(3, Math.floor(freeMonthlyOfferGenCount)));
    const remaining = 3 - used;
    return `Pozostało ${remaining} / 3 ofert`;
  }, [plan.isLoading, plan.canUseProFeatures, user?.id, freeMonthlyOfferGenCount]);

  const workshopStepIndex = resolveWorkshopStepIndex(showEstimate, estimateStage);
  const draftDisplayName = formatMvpDraftLabel(mvp);
  const draftShortId = draftId ? draftId.slice(0, 8).toUpperCase() : "NOWY";
  const templateTypeLabel =
    OFFER_DOCUMENT_MODE_OPTIONS.find((option) => option.value === templateType)?.title ?? "Decyzyjna";
  const workshopProgressPct = Math.round((workshopStepIndex / WORKSHOP_STEPS.length) * 100);
  const mvpChrome = useMvpWizardChrome();

  useEffect(() => {
    mvpChrome?.setChrome({
      draftDisplayName,
      draftShortId,
      workshopStepIndex,
      workshopProgressPct,
      lastSavedLabel: formatSavedAgo(lastSavedAt),
      // Nie ukrywaj workshop-shell — overlay jest wewnątrz Outlet; `hidden` na rodzicu
      // gasiłby cały podgląd mimo showPreviewOverlay === true.
      shellHidden: false,
    });
  }, [
    mvpChrome,
    draftDisplayName,
    draftShortId,
    workshopStepIndex,
    workshopProgressPct,
    lastSavedAt,
    showPreviewOverlay,
  ]);

  useEffect(() => {
    if (!mvpChrome) return;
    mvpChrome.registerPanelUi(null);
  }, [mvpChrome, showEstimate, estimateStage]);

  useEffect(() => {
    if (showEstimate) {
      setSidebarCollapsed(true);
    }
  }, [showEstimate]);

  useEffect(() => {
    if (!showEstimate) {
      setStep3DraftLimit(null);
      setEstimateStage(2);
      setTemplateFormatModalOpen(false);
      setPendingTemplateType(null);
      if (prevShowEstimateRef.current === true) {
        setShowPreviewOverlay(false);
        clearOfferPreviewPersistence();
      }
    }
    prevShowEstimateRef.current = showEstimate;
  }, [showEstimate, clearOfferPreviewPersistence]);

  /** Krok 3 → 4 (Założenia): kosztorys bez trybu skupienia; krok 4 w pasku = `!tableFocusMode` w `resolveWorkshopStepIndex`. */
  const goToClientAndTermsStep = useCallback(() => {
    if (showPreviewOverlay) closePreviewOverlay();
    setShowEstimate(true);
    setEstimateStage(3);
  }, [showPreviewOverlay, closePreviewOverlay]);

  const proceedFromEstimateToClientAndTerms = useCallback(async () => {
    setStep3DraftLimit(null);
    const userId = user?.id;
    if (!userId || plan.canUseProFeatures) {
      goToClientAndTermsStep();
      return;
    }

    setStep3ContinueChecking(true);
    try {
      const [draftRes, activeSub] = await Promise.all([
        supabase
          .from("drafts")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId),
        fetchActiveSubscriptionForUser(userId),
      ]);

      const draftCount = draftRes.error ? 0 : (draftRes.count ?? 0);
      if (draftCount >= 3 && !activeSub) {
        setStep3DraftLimit({ used: draftCount });
        return;
      }
      goToClientAndTermsStep();
    } finally {
      setStep3ContinueChecking(false);
    }
  }, [user?.id, plan.canUseProFeatures, goToClientAndTermsStep]);

  const persistDraftNow = useCallback(async () => {
    if (!draftId) return;
    const now = new Date().toISOString();
    const title = formatMvpDraftLabel(bundle);
    const { error } = await supabase
      .from("drafts")
      .update({
        data: bundle as unknown as Record<string, unknown>,
        title,
        updated_at: now,
      })
      .eq("id", draftId);
    if (error) {
      throw new Error(error.message);
    }
    setLastPersistedBundleSnapshot(bundleSnapshot);
  }, [draftId, bundle, bundleSnapshot]);

  const exitToDashboard = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  const handleExitCreator = useCallback(() => {
    if (hasUnsavedChanges) {
      setExitSaveError(null);
      setExitDialogOpen(true);
      return;
    }
    exitToDashboard();
  }, [hasUnsavedChanges, exitToDashboard]);

  const handleExitWithoutSaving = useCallback(() => {
    if (exitSaveInProgress) return;
    setExitDialogOpen(false);
    setExitSaveError(null);
    exitToDashboard();
  }, [exitSaveInProgress, exitToDashboard]);

  const handleExitWithSave = useCallback(async () => {
    setExitSaveInProgress(true);
    setExitSaveError(null);
    try {
      await persistDraftNow();
      setExitDialogOpen(false);
      exitToDashboard();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Nie udało się zapisać szkicu.";
      setExitSaveError(message);
    } finally {
      setExitSaveInProgress(false);
    }
  }, [persistDraftNow, exitToDashboard]);

  const navigateToWorkshopStep = useCallback(
    (targetStep: number) => {
      if (targetStep < 1 || targetStep > 3) return;
      if (!allMainStepsUnlocked && targetStep >= workshopStepIndex) return;

      if (showPreviewOverlay) {
        closePreviewOverlay();
      }
      setTemplateFormatModalOpen(false);
      setPendingTemplateType(null);

      if (targetStep === 1) {
        setShowEstimate(false);
        setWizard({ ...wizard, step: 0 });
        return;
      }

      if (targetStep === 2) {
        setShowEstimate(true);
        setEstimateStage(2);
        return;
      }

      setShowEstimate(true);
      setEstimateStage(3);
    },
    [
      allMainStepsUnlocked,
      workshopStepIndex,
      showPreviewOverlay,
      closePreviewOverlay,
      setShowEstimate,
      setEstimateStage,
      wizard,
      setWizard,
      lines.length,
    ]
  );

  const activeFlowIndex = !showEstimate
    ? Math.max(0, Math.min(2, wizard.step))
    : estimateStage === 2
      ? 3
      : 4;
  const flowLabels = useMemo(
    () => [...WORKSHOP_SCOPE_SUBSTEPS, WORKSHOP_STEPS[1], WORKSHOP_STEPS[2]],
    []
  );
  const prevFlowLabel = flowLabels[activeFlowIndex - 1] ?? null;
  const nextFlowLabel = flowLabels[activeFlowIndex + 1] ?? null;
  const canGoBackBottomNav = activeFlowIndex > 0;
  const isLastScopeSubstep = !showEstimate && wizard.step === 2;
  const isLastMainStep = showEstimate && estimateStage === 3;
  const bottomPrimaryLabel = isLastScopeSubstep
    ? "Generuj kosztorys →"
    : isLastMainStep
      ? "Wygeneruj ofertę →"
      : nextFlowLabel
        ? `${nextFlowLabel} →`
        : "Dalej →";
  const bottomPrimaryDisabled = !showEstimate
    ? (wizardNav?.primaryDisabled ?? true)
    : estimateStage === 2
      ? lines.length === 0 || step3ContinueChecking
      : offerGenSubmitting;

  const setClientDetailsField = useCallback(
    (field: "name" | "address" | "phone" | "email", value: string) => {
      setMvp((s) => ({
        ...s,
        clientDetails: {
          name: field === "name" ? value : s.clientDetails?.name ?? "",
          address: field === "address" ? value : s.clientDetails?.address ?? "",
          phone: field === "phone" ? value : s.clientDetails?.phone,
          email: field === "email" ? value : s.clientDetails?.email,
        },
      }));
      setStep4ClientError(null);
    },
    []
  );

  const handleMaterialsOwnershipChange = useCallback((value: MaterialsOwnership | undefined) => {
    setMvp((s) => {
      if (value) return { ...s, materialsOwnership: value };
      const { materialsOwnership: _removed, ...rest } = s;
      return rest;
    });
    setStep4ClientError(null);
  }, []);

  const handleWarrantyTypeChange = useCallback((value: string | undefined) => {
    setMvp((s) => {
      const trimmed = value?.trim();
      if (trimmed) return { ...s, warrantyType: trimmed };
      const { warrantyType: _removed, ...rest } = s;
      return rest;
    });
  }, []);

  const handleExclusionsChange = useCallback((next: string[] | undefined) => {
    setMvp((s) => {
      if (next && next.length > 0) return { ...s, exclusions: next };
      const { exclusions: _removed, ...rest } = s;
      return rest;
    });
  }, []);

  const handlePaymentTermsChange = useCallback((value: string | undefined) => {
    setMvp((s) => {
      const trimmed = value?.trim();
      if (trimmed) return { ...s, paymentTerms: trimmed };
      const { paymentTerms: _removed, ...rest } = s;
      return rest;
    });
  }, []);

  const handleOfferAssumptionsChange = useCallback((next: string[] | undefined) => {
    setMvp((s) => {
      if (next && next.length > 0) return { ...s, offerAssumptions: next };
      const { offerAssumptions: _removed, ...rest } = s;
      return rest;
    });
  }, []);

  const handleStep3SectionIncludesChange = useCallback(
    (next: ReturnType<typeof normalizeStep3SectionIncludes>) => {
      setMvp((s) => ({ ...s, step3SectionIncludes: next }));
    },
    []
  );

  const handleStep3ExtrasChange = useCallback((patch: Partial<Step3OfferExtras>) => {
    setMvp((s) => {
      const next: Step3OfferExtras = { ...(s.step3Extras ?? {}), ...patch };
      const compact = Object.fromEntries(
        Object.entries(next).filter(([, value]) => {
          if (typeof value === "string") return value.trim().length > 0;
          if (Array.isArray(value)) return value.length > 0;
          return value !== undefined;
        })
      ) as Step3OfferExtras;
      return { ...s, step3Extras: compact };
    });
  }, []);

  const handleBottomBack = () => {
    if (templateFormatModalOpen) {
      setTemplateFormatModalOpen(false);
      setPendingTemplateType(null);
      return;
    }

    if (!canGoBackBottomNav) return;

    if (showPreviewOverlay) {
      closePreviewOverlay();
      return;
    }

    if (!showEstimate) {
      wizardNav?.onBack();
      return;
    }

    if (estimateStage === 2) {
      setShowEstimate(false);
      setWizard({ ...wizard, step: 2 });
      return;
    }

    setEstimateStage(2);
  };

  const handleBottomPrimary = () => {
    if (bottomPrimaryDisabled) return;

    if (!showEstimate) {
      wizardNav?.onPrimary();
      return;
    }

    if (estimateStage === 2) {
      void proceedFromEstimateToClientAndTerms();
      return;
    }

    const nameOk = (clientDetails?.name ?? "").trim().length > 0;
    const addrOk = (clientDetails?.address ?? "").trim().length > 0;
    if (!nameOk || !addrOk) {
      setStep4ClientError("Uzupełnij dane adresata w kroku „Klient i warunki”, aby wygenerować ofertę.");
      return;
    }
    if (step3Includes.materials && !materialsOwnership) {
      setStep4ClientError(STEP3_MATERIALS_REQUIRED_MSG);
      return;
    }

    setStep4ClientError(null);
    setOfferGenPreviewBlockReason(null);
    void openPreviewOverlayAfterGate();
  };

  const handleTemplateFormatModalBack = () => {
    setTemplateFormatModalOpen(false);
    setPendingTemplateType(null);
  };

  const handleTemplateFormatModalNext = () => {
    if (!pendingTemplateType) return;
    handleDocumentLayoutTypeChange(pendingTemplateType);
    setTemplateFormatModalOpen(false);
    setPendingTemplateType(null);
    if (templateFormatModalIntent === "generate") {
      void openPreviewOverlayAfterGate();
    }
  };

  if (!draftReady) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background"
      >
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--ink-3)" }} />
      </div>
    );
  }

  if (invalidDraft) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-5 text-center"
      >
        <p className="text-foreground">Nie znaleziono tego szkicu.</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button asChild variant="outline">
            <Link to="/dashboard">← Panel</Link>
          </Button>
          <Button asChild>
            <Link to="/mvp/new">Nowa oferta</Link>
          </Button>
        </div>
      </div>
    );
  }

  const previewOverlayNode =
    showPreviewOverlay ? (
        <div
          className="client-offer-preview-overlay fixed inset-0 z-[100] overflow-y-auto p-5 sm:p-8 md:p-10 print:static print:inset-auto print:z-auto print:block print:h-auto print:min-h-0 print:overflow-visible print:p-0"
          style={{ background: "var(--color-bg-page)" }}
        >
          <div className="mx-auto w-full max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl print:max-w-none print:h-auto print:min-h-0">
            {!offerGenBlockedView ? (
              <OfferPreviewWithTemplateSidebar
                templateId={templateId}
                onTemplateChange={handleTemplateChange}
              >
              <ClientOfferView
                lines={offerRows}
                offerTexts={offerTextsForDocument}
                draftId={draftId}
                clientName={clientDetails?.name}
                clientAddress={clientDetails?.address}
                clientPhone={clientDetails?.phone}
                clientEmail={clientDetails?.email}
                templateId={templateId}
                templateType={templateType}
                showBuilderBranding={!plan.canUseProFeatures}
                companyNameReminder={shouldWarnMissingCompanyName}
                companyDataLoading={companyProfileLoading}
                companyName={offerCompanyNameForHeader}
                companyNip={companyProfileOffer?.nip}
                companyAddress={companyProfileOffer?.address}
                companyPhone={companyProfileOffer?.phone}
                companyEmail={companyProfileOffer?.email}
                companyLogoUrl={companyProfileOffer?.logoUrl}
                onBackToEdit={closePreviewOverlay}
                onOfferTextsChange={(patch) =>
                  setMvp((s) => ({ ...s, offerTexts: applyOfferTextsPatch(s.offerTexts, patch) }))
                }
                offerAssumptions={offerAssumptions ?? []}
                onOfferAssumptionsChange={(next) => setMvp((s) => ({ ...s, offerAssumptions: next }))}
                onPaymentTermsChange={handlePaymentTermsChange}
                onWarrantyTypeChange={handleWarrantyTypeChange}
                onExclusionsChange={handleExclusionsChange}
                materialsOwnership={materialsOwnership}
                materialsNotes={materialsNotes}
                paymentTerms={paymentTerms}
                warrantyType={warrantyType}
                exclusions={exclusions ?? []}
                step3SectionIncludes={step3Includes}
                shareActionSlot={null}
                sharePanelSlot={
                  <>
                    {sharePaywall ? (
                      <div id="mvp-share-paywall-hint" className="w-full max-w-lg">
                        <UpgradePrompt
                          variant="sm"
                          authenticated={user != null}
                          canStartTrial={user != null && plan.canStartTrial}
                          onStartTrial={() => void plan.startTrialForUser()}
                          onGoPro={() => navigate("/ustawienia")}
                          onLogin={() => navigate("/login", { state: { returnTo: location.pathname } })}
                          className="text-left"
                        />
                      </div>
                    ) : null}
                    {!sharePaywall && user ? (
                      <div className="w-full max-w-lg space-y-2">
                        {shareBusy ? (
                          <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            Przygotowuję link udostępniania…
                          </p>
                        ) : null}
                        {shareState === "error" && shareError ? (
                          <p className="text-sm text-destructive">{shareError}</p>
                        ) : null}
                        {shareLinkUrl ? (
                          <>
                            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
                              <Input
                                readOnly
                                value={shareLinkUrl}
                                className="font-mono text-xs sm:min-w-0 sm:max-w-[min(100%,28rem)] sm:flex-1"
                                aria-label="Publiczny link do oferty"
                              />
                              <Button
                                type="button"
                                variant="secondary"
                                className="shrink-0 sm:w-auto"
                                onClick={() => void handleCopyShareLink()}
                              >
                                {shareCopied ? "Skopiowano!" : "Kopiuj link"}
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Link jest aktywny — klient zobaczy aktualną wersję oferty
                            </p>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                }
              />
              </OfferPreviewWithTemplateSidebar>
            ) : offerGenBlockedView ? (
              <section className="no-print rounded-lg border border-border/60 bg-card/80 px-4 py-6 text-sm shadow-sm sm:px-6">
                <h2 className="text-base font-semibold text-foreground">Limit ofert (plan Free)</h2>
                <p className="mt-3 text-sm text-foreground/90">Wykorzystałeś limit 3 ofert w tym miesiącu.</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={closePreviewOverlay}>
                    Wróć do edycji
                  </Button>
                  <Button type="button" variant="action" size="lg" asChild>
                    <Link
                      to="/pricing"
                      onClick={() => {
                        console.log("POSTHOG EVENT:", "unlock_cta_clicked");
                        trackEvent("unlock_cta_clicked");
                      }}
                    >
                      🔓 Odblokuj nielimitowane oferty — 79 zł/mies →
                    </Link>
                  </Button>
                </div>
                <p className="mt-2 max-w-md text-xs text-muted-foreground">
                  Limit odnowi się {formatPolishRenewalFirstOfNextMonth()}.
                </p>
              </section>
            ) : offerGenRpcError ? (
              <section className="no-print rounded-lg border border-destructive/40 bg-card/80 px-4 py-6 text-sm shadow-sm sm:px-6">
                <h2 className="text-base font-semibold text-foreground">Nie udało się wygenerować oferty</h2>
                <p className="mt-3 text-sm text-destructive" role="alert">
                  {offerGenRpcError}
                </p>
                <Button type="button" variant="outline" className="mt-4" onClick={closePreviewOverlay}>
                  Wróć do edycji
                </Button>
              </section>
            ) : (
              <section className="no-print rounded-lg border border-border/60 bg-card/80 px-4 py-4 text-sm shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Krok 5 z 5 — Dane do oferty
                </p>
                <h2 className="mt-1 text-base font-semibold text-foreground">Dane klienta</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Uzupełnij wymagane pola, aby wygenerować podgląd oferty.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label htmlFor="client-name" className="text-xs">
                      Imię i nazwisko / nazwa firmy klienta *
                    </Label>
                    <Input
                      id="client-name"
                      value={clientDetails?.name ?? ""}
                      onChange={(e) =>
                        setMvp((s) => ({
                          ...s,
                          clientDetails: {
                            name: e.target.value,
                            address: s.clientDetails?.address ?? "",
                            phone: s.clientDetails?.phone,
                            email: s.clientDetails?.email,
                          },
                        }))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="client-address" className="text-xs">
                      Adres inwestycji *
                    </Label>
                    <textarea
                      id="client-address"
                      value={clientDetails?.address ?? ""}
                      onChange={(e) =>
                        setMvp((s) => ({
                          ...s,
                          clientDetails: {
                            name: s.clientDetails?.name ?? "",
                            address: e.target.value,
                            phone: s.clientDetails?.phone,
                            email: s.clientDetails?.email,
                          },
                        }))
                      }
                      className="mt-1 min-h-[72px] w-full rounded-md border border-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      style={{ background: "var(--color-bg-input)", color: "var(--color-text-primary)" }}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="client-phone" className="text-xs">
                      Telefon klienta
                    </Label>
                    <Input
                      id="client-phone"
                      value={clientDetails?.phone ?? ""}
                      onChange={(e) =>
                        setMvp((s) => ({
                          ...s,
                          clientDetails: {
                            name: s.clientDetails?.name ?? "",
                            address: s.clientDetails?.address ?? "",
                            phone: e.target.value,
                            email: s.clientDetails?.email,
                          },
                        }))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="client-email" className="text-xs">
                      E-mail klienta
                    </Label>
                    <Input
                      id="client-email"
                      value={clientDetails?.email ?? ""}
                      onChange={(e) =>
                        setMvp((s) => ({
                          ...s,
                          clientDetails: {
                            name: s.clientDetails?.name ?? "",
                            address: s.clientDetails?.address ?? "",
                            phone: s.clientDetails?.phone,
                            email: e.target.value,
                          },
                        }))
                      }
                      className="mt-1"
                    />
                  </div>
                  <p className="sm:col-span-2 mt-1 text-xs text-muted-foreground">
                    * pola wymagane
                  </p>
                  {offerGenRpcError ? (
                    <p className="sm:col-span-2 text-sm text-destructive" role="alert">
                      {offerGenRpcError}
                    </p>
                  ) : null}
                </div>
              </section>
            )}
          </div>
        </div>
      ) : null;

  const templateFormatModalNode =
    templateFormatModalOpen ? (
      <div
        className="fixed inset-0 z-[110] flex items-center justify-center overflow-y-auto p-4 sm:p-6"
        style={{ background: "rgba(0,0,0,0.6)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-format-modal-title"
        aria-describedby="template-format-modal-description"
      >
        <div
          className="w-full bg-background shadow-2xl"
          style={{ maxWidth: 1120, borderRadius: 16 }}
        >
          <div className="border-b border-border px-5 py-5 sm:px-6">
            <h2 id="template-format-modal-title" className="text-xl font-semibold tracking-tight text-foreground">
              Wybierz format oferty
            </h2>
            <p id="template-format-modal-description" className="mt-1 text-sm text-muted-foreground">
              Możesz zmienić format w dowolnym momencie w kreatorze.
            </p>
          </div>

          <div className="px-5 py-5 sm:px-6">
            <OfferDocumentModeCards value={pendingTemplateType} onChange={setPendingTemplateType} />
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <Button type="button" variant="outline" onClick={handleTemplateFormatModalBack}>
              Wróć
            </Button>
            <Button type="button" onClick={handleTemplateFormatModalNext} disabled={!pendingTemplateType}>
              Dalej →
            </Button>
          </div>
        </div>
      </div>
    ) : null;
  return (
    <>
      {typeof document !== "undefined" && previewOverlayNode
        ? createPortal(previewOverlayNode, document.body)
        : null}
      {typeof document !== "undefined" && templateFormatModalNode
        ? createPortal(templateFormatModalNode, document.body)
        : null}

          <header className="workshop-topbar no-print">
            <div className="workshop-topbar-main">
              <Button
                type="button"
                variant="ghost"
                className="workshop-btn-ghost workshop-topbar-exit"
                onClick={handleExitCreator}
              >
                ← Anuluj
              </Button>
              <p className="workshop-breadcrumb">
                <Link to="/dashboard/oferty">Oferty</Link> / <strong>{draftShortId}</strong>
              </p>
            </div>
            <div className="workshop-topbar-actions">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 rounded-full border-[#c7541f]/40 px-3 text-xs font-semibold text-foreground hover:border-[#c7541f] hover:bg-[#c7541f] hover:text-white"
                onClick={openTemplateTypeSelector}
                aria-label={`Zmień format oferty. Aktualnie: ${templateTypeLabel}`}
              >
                {templateTypeLabel}
              </Button>
              <button
                type="button"
                onClick={toggleTheme}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border shadow-sm transition-opacity hover:opacity-95"
                style={{
                  borderColor: theme === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.14)",
                  background: theme === "dark" ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.06)",
                  color: theme === "dark" ? "#fff" : "#111827",
                }}
                aria-label={theme === "dark" ? "Przełącz na jasny motyw" : "Przełącz na ciemny motyw"}
                title={theme === "dark" ? "Jasny motyw" : "Ciemny motyw"}
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="workshop-user-menu-trigger"
                    aria-label="Menu użytkownika"
                  >
                    <span className="workshop-user-menu-avatar" aria-hidden>
                      {user?.email ? userInitials(user.email) : <User2 className="h-4 w-4" />}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onSelect={() => navigate("/ustawienia")}>
                    <Settings className="mr-2 h-4 w-4" aria-hidden />
                    Ustawienia
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/")}>Moje oferty</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => void signOut()}>
                    <LogOut className="mr-2 h-4 w-4" aria-hidden />
                    Wyloguj
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <nav className="workshop-steps no-print" aria-label="Kroki kreatora">
            {WORKSHOP_STEPS.map((label, idx) => {
              const n = idx + 1;
              const state =
                n < workshopStepIndex ? "done" : n === workshopStepIndex ? "active" : "todo";
              const stepClassName = cn(
                "workshop-step",
                state === "done" && "is-done",
                state === "active" && "is-active"
              );
              const stepContent = (
                <>
                  <span className="workshop-step-index" aria-hidden>
                    {state === "done" ? "✓" : n}
                  </span>
                  {label}
                </>
              );
              const isStepClickable =
                state === "done" || (allMainStepsUnlocked && state === "todo");
              if (isStepClickable) {
                return (
                  <button
                    key={label}
                    type="button"
                    className={stepClassName}
                    onClick={() => navigateToWorkshopStep(n)}
                    aria-label={
                      state === "done" ? `Wróć do kroku: ${label}` : `Przejdź do kroku: ${label}`
                    }
                  >
                    {stepContent}
                  </button>
                );
              }
              return (
                <div
                  key={label}
                  className={stepClassName}
                  aria-current={state === "active" ? "step" : undefined}
                >
                  {stepContent}
                </div>
              );
            })}
          </nav>
          {!showEstimate ? (
            <div className="workshop-substeps-progress no-print" aria-label="Postęp sub-kroków Zakresu prac">
              {WORKSHOP_SCOPE_SUBSTEPS.map((label, idx) => (
                <div
                  key={label}
                  className={cn("workshop-substep-segment", idx <= wizard.step && "is-active")}
                  aria-hidden
                />
              ))}
            </div>
          ) : null}

          <div className="workshop-body">
            {!showEstimate ? (
              <div className="workshop-body-inner flex min-h-0 w-full flex-1 flex-col">
                <div className="mb-5 max-w-md">
                  <Label htmlFor="mvp-draft-label" className="text-xs" style={{ color: "var(--ink-3)" }}>
                    Robocza nazwa szkicu (opcjonalnie)
                  </Label>
                  <Input
                    id="mvp-draft-label"
                    type="text"
                    placeholder="np. Remont — ul. Lipowa 12"
                    value={draftLabel ?? ""}
                    onChange={(e) => setMvp((s) => ({ ...s, draftLabel: e.target.value }))}
                    className="mt-1.5 h-10 border-[var(--line)] bg-[var(--paper)]"
                    maxLength={120}
                    autoComplete="off"
                  />
                </div>
                <section className="workshop-wizard-card relative flex w-full flex-col">
                  {isGenerating && (
                    <div
                      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-lg bg-[var(--paper)]/90 backdrop-blur-[2px]"
                      role="status"
                      aria-live="polite"
                    >
                      <Loader2 className="h-10 w-10 animate-spin" style={{ color: "var(--ochre)" }} />
                      <p className="text-sm font-medium" style={{ color: "var(--ink)" }}>
                        Generowanie kosztorysu…
                      </p>
                    </div>
                  )}
                  <MvpWizard
                    wizard={wizard}
                    onChange={setWizard}
                    onComplete={handleGenerate}
                    onNavActionsChange={setWizardNav}
                    hideFooterNav
                  />
                </section>
              </div>
            ) : estimateStage === 2 ? (
              <div
                className={cn(
                  "workshop-editor min-w-0 flex-1",
                  tableFocusMode ? "space-y-3" : "space-y-6"
                )}
              >
<div className="min-w-0">
                      <h2 className="text-xl font-semibold tracking-tight text-foreground">Kosztorys</h2>
                      <p className="mt-2 text-sm text-foreground/85">
                        {totalArea} m² ·{" "}
                        {parseWorkTypes(wizard.workType)
                          .map((wt) => WORK_TYPE_LABELS[wt])
                          .join(" + ")}{" "}
                        · {STANDARD_LABEL[wizard.standard]} · {regionLabel}
                        {wizard.projectDescription?.trim() ? (
                          <>
                            {" "}
                            · <span className="text-foreground/90">{wizard.projectDescription.trim()}</span>
                          </>
                        ) : null}
                      </p>
                      {wizard.projectNotes?.trim() ? (
                        <p className="mt-3 max-w-3xl border-l-2 border-primary/30 pl-3 text-sm text-foreground/90">
                          <span className="font-medium text-foreground">Notatka: </span>
                          {wizard.projectNotes.trim()}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex w-full shrink-0 flex-col gap-1.5 sm:w-auto sm:min-w-[12rem] sm:items-end">
                      {freeOfferGenPill ? (
                        <span
                          className={cn(
                            "inline-flex max-w-[min(100%,22rem)] flex-wrap items-center justify-center rounded-full border px-2 py-0.5 text-center text-xs font-medium leading-snug sm:justify-end",
                            freeOfferGenPill.className
                          )}
                        >
                          {freeOfferGenPill.kind === "plain" ? (
                            freeOfferGenPill.text
                          ) : (
                            <>
                              {freeOfferGenPill.lead}
                            <a
                              href="#"
                              className="font-semibold underline underline-offset-2 hover:no-underline"
                              onClick={() => {
                                console.log("POSTHOG EVENT:", "unlock_cta_clicked");
                                trackEvent("unlock_cta_clicked");
                              }}
                            >
                              Odblokuj
                            </a>
                            </>
                          )}
                        </span>
                      ) : null}
                      <div className="flex w-full flex-col items-stretch gap-2 sm:items-end">
                        <div className="flex w-full flex-wrap gap-2 sm:justify-end">
                          <Button type="button" variant="outline" onClick={() => setShowEstimate(false)}>
                            Zmień dane wejściowe
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={isGenerating}
                            onClick={() => void handleRestorePricesFromTemplates()}
                          >
                            {isGenerating ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Przeliczanie…
                              </>
                            ) : (
                              "Przywróć ceny z bazy"
                            )}
                          </Button>
                        </div>
                        {shouldWarnMissingCompanyName ? (
                          <p
                            className="w-full max-w-lg text-right text-xs leading-relaxed"
                            style={{ color: "var(--color-warning)" }}
                          >
                            Twoja oferta nie ma nazwy firmy — klient zobaczy pustą sekcję wykonawcy.{" "}
                            <Link
                              to="/ustawienia"
                              className="font-medium text-foreground underline underline-offset-2 hover:no-underline"
                            >
                              Uzupełnij dane firmy →
                            </Link>
                          </p>
                        ) : null}
                      </div>
                    </div>

                <section
                  className={cn(
                    "rounded-xl border border-border/60 shadow-sm",
                    tableFocusMode ? "px-3 py-2.5 sm:px-4" : "px-3 py-3 sm:px-4 sm:py-3.5",
                    !vatSummaryExpanded && "py-2.5 sm:py-2.5"
                  )}
                  style={{ background: "var(--color-bg-surface)" }}
                  {...(vatSummaryExpanded
                    ? ({ "aria-labelledby": "mvp-vat-summary-heading" } as const)
                    : ({
                        "aria-label":
                          "Podsumowanie VAT — zwinięte: sumy netto, VAT i brutto, wybór stawki i narzucenie na pozycje",
                      } as const))}
                >
                  {!vatSummaryExpanded ? (
                    <>
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-3">
                        <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-2 text-xs sm:text-sm">
                          <div className="font-mono tabular-nums">
                            <span className="text-muted-foreground">Netto </span>
                            <span className="font-semibold text-primary">
                              {vatSummary.netTotal.toLocaleString("pl-PL", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              zł
                            </span>
                          </div>
                          <div className="font-mono tabular-nums text-foreground">
                            <span className="text-muted-foreground">VAT </span>
                            {vatSummary.vatAmount.toLocaleString("pl-PL", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            zł
                          </div>
                          <div className="font-mono tabular-nums">
                            <span className="text-muted-foreground">Brutto </span>
                            <span className="text-base font-bold text-foreground">
                              {vatSummary.grossTotal.toLocaleString("pl-PL", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              zł
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-end gap-2 sm:justify-end">
                          <div className="min-w-[10.5rem] flex-1 sm:flex-initial sm:min-w-[11rem]">
                            <label htmlFor="mvp-vat-mode-bar" className="sr-only">
                              Stawka do narzucenia (wszystkie pozycje)
                            </label>
                            <Select value={vatMode} onValueChange={(v) => setVatMode(v as VatMode)}>
                              <SelectTrigger
                                id="mvp-vat-mode-bar"
                                className="h-9 bg-background"
                                aria-label="Stawka VAT do narzucenia"
                              >
                                <SelectValue placeholder="Wybierz" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Bez VAT</SelectItem>
                                <SelectItem value="8">VAT 8%</SelectItem>
                                <SelectItem value="23">VAT 23%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="h-9 shrink-0 sm:min-w-[10.5rem]"
                            onClick={applyVatToAllLines}
                            disabled={lines.length === 0}
                          >
                            Narzuć wybraną stawkę na wszystkie pozycje
                          </Button>
                          {hasMultipleVatRates ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-9 shrink-0 gap-1.5 font-medium"
                              aria-expanded={false}
                              onClick={() => setVatSummaryExpanded(true)}
                            >
                              <ChevronDown className="h-4 w-4 opacity-80" aria-hidden />
                              Rozwiń podsumowanie
                            </Button>
                          ) : null}
                        </div>
                      </div>
                      {linesVatMismatchOffer && lines.length > 0 ? (
                        <p
                          className="mt-2 rounded-md border px-2.5 py-1.5 text-xs"
                          style={{
                            borderColor: "color-mix(in srgb, var(--color-warning) 35%, transparent)",
                            background: "color-mix(in srgb, var(--color-warning) 10%, transparent)",
                            color: "var(--color-text-primary)",
                          }}
                          role="status"
                        >
                          Wybrana stawka do narzucenia różni się od co najmniej jednej pozycji w tabeli — użyj przycisku
                          powyżej, aby ujednolicić.
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <h3 id="mvp-vat-summary-heading" className="text-sm font-semibold text-foreground">
                          Podsumowanie: netto · VAT · brutto
                        </h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-9 shrink-0 gap-1.5 font-medium"
                          aria-expanded={vatSummaryExpanded}
                          onClick={() => setVatSummaryExpanded(false)}
                        >
                          <ChevronUp className="h-4 w-4 opacity-80" aria-hidden />
                          Zwiń
                        </Button>
                      </div>
                      <div
                        className={cn(
                          "mt-3 flex flex-col sm:flex-row sm:items-start sm:justify-between",
                          tableFocusMode ? "gap-2 sm:items-center sm:gap-4" : "gap-3 sm:gap-4"
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          {!tableFocusMode ? (
                            <>
                              <p className="text-[11px] leading-snug text-muted-foreground">
                                Sumy zgodne z kolumnami w tabeli.
                              </p>
                              <details className="mt-1.5 rounded-md border border-border/50 bg-muted/20 px-2 py-1.5 text-[11px] text-muted-foreground">
                                <summary className="cursor-pointer select-none text-xs font-medium text-foreground/90 marker:text-muted-foreground">
                                  Pokaż szczegóły VAT
                                </summary>
                                <div className="mt-2 space-y-2 border-t border-border/40 pt-2 leading-relaxed">
                                  <p>
                                    W tabeli: netto pozycji → kwota VAT (stawka pod kwotą) → brutto pozycji. Poniższe sumy
                                    = sumy z tych samych kolumn.
                                  </p>
                                  <p>
                                    Lista wybiera tylko stawkę docelową. Aby zapisać ją na liniach, użyj przycisku.
                                    Pojedyncze pozycje możesz zmienić w kolumnie VAT w tabeli.
                                  </p>
                                </div>
                              </details>
                            </>
                          ) : null}
                        </div>
                        <div
                          className={cn(
                            "flex w-full flex-col",
                            tableFocusMode
                              ? "gap-3 sm:w-[min(100%,280px)] sm:min-w-0 sm:flex-row sm:flex-wrap sm:items-end sm:gap-2"
                              : "gap-2 sm:w-auto sm:min-w-[13.5rem] sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-2 sm:gap-y-1.5 md:max-w-[min(100%,20rem)]"
                          )}
                        >
                          <div className={cn(tableFocusMode && "min-w-[10.5rem] flex-1", !tableFocusMode && "min-w-[11rem]")}>
                            <label
                              htmlFor="mvp-vat-mode"
                              className="mb-1 block text-xs font-medium text-foreground/80"
                            >
                              Stawka do narzucenia (wszystkie pozycje)
                            </label>
                            <Select value={vatMode} onValueChange={(v) => setVatMode(v as VatMode)}>
                              <SelectTrigger
                                id="mvp-vat-mode"
                                className="h-9 bg-background"
                                aria-label="Stawka VAT do narzucenia"
                              >
                                <SelectValue placeholder="Wybierz" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Bez VAT</SelectItem>
                                <SelectItem value="8">VAT 8%</SelectItem>
                                <SelectItem value="23">VAT 23%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={cn(
                              "w-full shrink-0 justify-center",
                              tableFocusMode && "sm:w-auto sm:min-w-[7rem]",
                              !tableFocusMode && "h-9 sm:min-w-[10.5rem]"
                            )}
                            onClick={applyVatToAllLines}
                            disabled={lines.length === 0}
                          >
                            Narzuć wybraną stawkę na wszystkie pozycje
                          </Button>
                        </div>
                      </div>

                      {linesVatMismatchOffer && lines.length > 0 ? (
                        <p
                          className={cn(
                            "rounded-md border px-2.5 py-1.5 text-xs",
                            tableFocusMode ? "mt-2" : "mt-2.5"
                          )}
                          style={{
                            borderColor: "color-mix(in srgb, var(--color-warning) 35%, transparent)",
                            background: "color-mix(in srgb, var(--color-warning) 10%, transparent)",
                            color: "var(--color-text-primary)",
                          }}
                          role="status"
                        >
                          Wybrana stawka do narzucenia różni się od co najmniej jednej pozycji w tabeli — użyj przycisku
                          powyżej, aby ujednolicić.
                        </p>
                      ) : null}

                      <dl
                        className={cn(
                          "border-t border-border/50 text-sm",
                          tableFocusMode
                            ? "mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-1 border-border/40 pt-2"
                            : "mt-3 space-y-1.5 border-border/40 pt-2.5"
                        )}
                      >
                        <div
                          className={cn(
                            "flex justify-between gap-4",
                            tableFocusMode && "min-w-[10rem] flex-1 sm:flex-none"
                          )}
                        >
                          <dt className="font-medium text-foreground">Wartość netto (razem)</dt>
                          <dd className="font-mono text-sm font-semibold tabular-nums text-primary">
                            {vatSummary.netTotal.toLocaleString("pl-PL", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            zł
                          </dd>
                        </div>
                        <div
                          className={cn(
                            "flex justify-between gap-4",
                            tableFocusMode && "min-w-[8rem] flex-1 sm:flex-none"
                          )}
                        >
                          <dt className="text-muted-foreground">VAT</dt>
                          <dd className="font-mono tabular-nums text-foreground">
                            {vatSummary.vatAmount.toLocaleString("pl-PL", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            zł
                          </dd>
                        </div>
                        <div
                          className={cn(
                            "flex justify-between gap-4",
                            tableFocusMode
                              ? "min-w-[12rem] flex-[1_1_100%] border-t border-border/30 pt-2 sm:flex-[1_1_auto] sm:border-t-0 sm:pt-0"
                              : "border-t border-border/35 pt-2"
                          )}
                        >
                          <dt className="text-sm font-semibold text-foreground">Wartość brutto (do zapłaty)</dt>
                          <dd className="font-mono text-base font-bold tabular-nums text-foreground">
                            {vatSummary.grossTotal.toLocaleString("pl-PL", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            zł
                          </dd>
                        </div>
                      </dl>
                    </>
                  )}
                </section>

                {incompleteLines.length > 0 ? (
                  <section
                    className="rounded-xl border border-border/60 px-3 py-2.5 text-sm shadow-sm"
                    style={{
                      background: "color-mix(in srgb, var(--color-warning) 8%, var(--color-bg-surface))",
                    }}
                    aria-label="Pozycje do uzupełnienia"
                  >
                    <p className="text-sm font-semibold" style={{ color: "var(--color-warning)" }}>
                      Uwaga: do uzupełnienia przed wysłaniem: {incompleteLines.length}{" "}
                      {incompleteLines.length === 1 ? "pozycja" : "pozycji"}
                    </p>
                    <ul className="mt-2 space-y-1">
                      {incompleteLines.map((l) => (
                        <li key={l.id}>
                          <button
                            type="button"
                            className="w-full truncate text-left text-primary underline-offset-2 hover:underline"
                            onClick={() => scrollToLine(l.id)}
                            title={l.name}
                          >
                            {l.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                <div
                  ref={tableWorkRegionRef}
                  id="mvp-estimate-table-work-region"
                  className="scroll-mt-4"
                >
                  {recalcHint ? (
                    <p className="text-sm text-muted-foreground" role="status">
                      {recalcHint}
                    </p>
                  ) : null}
                  <MvpEstimateTable
                    lines={offerRows}
                    onChange={setOfferRows}
                    onAddRow={handleAddRow}
                    onAddRowFromTemplate={handleAddRowFromTemplate}
                    loadAddRowTemplates={loadAddRowTemplates}
                    onRestoreRowPrice={(lineId) => void handleRestoreRowPrice(lineId)}
                    focusMode={tableFocusMode}
                    onFocusModeChange={setTableFocusMode}
                    workshopLayout
                  />
                </div>
              </div>
            ) : estimateStage === 3 ? (
              <div className="workshop-body-inner flex min-h-0 flex-1 flex-col">
                <MvpStep3ClientConditions
                  clientDetails={clientDetails}
                  onClientFieldChange={setClientDetailsField}
                  materialsOwnership={materialsOwnership}
                  onMaterialsOwnershipChange={handleMaterialsOwnershipChange}
                  warrantyType={warrantyType}
                  onWarrantyTypeChange={handleWarrantyTypeChange}
                  paymentTerms={paymentTerms}
                  onPaymentTermsChange={handlePaymentTermsChange}
                  templateType={templateType}
                  timelineNote={mvp.offerTexts?.timelineNote}
                  onTimelineNoteChange={(value) =>
                    setMvp((s) => {
                      const nextTexts = { ...(s.offerTexts ?? {}) };
                      if (value === undefined || !value.trim()) {
                        delete nextTexts.timelineNote;
                      } else {
                        nextTexts.timelineNote = value.trim();
                      }
                      return { ...s, offerTexts: nextTexts };
                    })
                  }
                  exclusions={exclusions}
                  onExclusionsChange={handleExclusionsChange}
                  offerAssumptions={offerAssumptions}
                  onOfferAssumptionsChange={handleOfferAssumptionsChange}
                  step3SectionIncludes={step3SectionIncludes}
                  onStep3SectionIncludesChange={handleStep3SectionIncludesChange}
                  step3Extras={step3Extras}
                  onStep3ExtrasChange={handleStep3ExtrasChange}
                  step4ClientError={step4ClientError}
                  step3DraftLimit={step3DraftLimit}
                />
                {offerGenPreviewBlockReason ? (
                  <p className="mt-4 text-sm text-destructive" role="alert">
                    {offerGenPreviewBlockReason}
                  </p>
                ) : null}
                {offerGenRpcError ? (
                  <p className="mt-2 text-sm text-destructive" role="alert">
                    {offerGenRpcError}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <footer
            className={cn(
              "workshop-bottom-nav no-print",
              isLastMainStep && "workshop-bottom-nav--step4"
            )}
          >
            <Button
              type="button"
              variant="ghost"
              onClick={handleBottomBack}
              disabled={!canGoBackBottomNav}
              className="justify-start"
            >
              {prevFlowLabel ? `← ${prevFlowLabel}` : "←"}
            </Button>
            {isLastMainStep ? (
              <div className="workshop-step4-cta-row">
                {step4OfferQuotaLabel ? (
                  <p className="text-sm font-medium text-muted-foreground">{step4OfferQuotaLabel}</p>
                ) : (
                  <span />
                )}
                <Button
                  type="button"
                  onClick={handleBottomPrimary}
                  disabled={bottomPrimaryDisabled}
                  className="workshop-btn-next shrink-0"
                >
                  {offerGenSubmitting ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                      Generowanie…
                    </>
                  ) : (
                    bottomPrimaryLabel
                  )}
                </Button>
              </div>
            ) : !showEstimate ? (
              <span className="workshop-substep-counter" aria-live="polite">
                {wizard.step + 1} / {WORKSHOP_SCOPE_SUBSTEPS.length}
              </span>
            ) : (
              <span />
            )}
            {!isLastMainStep ? (
              <Button
                type="button"
                onClick={handleBottomPrimary}
                disabled={bottomPrimaryDisabled}
                className="workshop-btn-next justify-end"
              >
                {estimateStage === 2 && step3ContinueChecking ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                    Sprawdzanie…
                  </>
                ) : (
                  bottomPrimaryLabel
                )}
              </Button>
            ) : null}
          </footer>
          <AlertDialog
            open={exitDialogOpen}
            onOpenChange={(open) => {
              if (exitSaveInProgress) return;
              setExitDialogOpen(open);
              if (!open) setExitSaveError(null);
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Czy zapisać szkic przed wyjściem?</AlertDialogTitle>
                <AlertDialogDescription>
                  Niezapisane zmiany zostaną utracone, jeśli wyjdziesz bez zapisywania.
                </AlertDialogDescription>
              </AlertDialogHeader>
              {exitSaveError ? (
                <p className="text-sm text-destructive" role="alert">
                  {exitSaveError}
                </p>
              ) : null}
              <AlertDialogFooter>
                <Button type="button" onClick={() => void handleExitWithSave()} disabled={exitSaveInProgress}>
                  {exitSaveInProgress ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                      Zapisywanie...
                    </>
                  ) : (
                    "Zapisz szkic"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleExitWithoutSaving}
                  disabled={exitSaveInProgress}
                >
                  Wyjdź bez zapisywania
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setExitDialogOpen(false)}
                  disabled={exitSaveInProgress}
                >
                  Anuluj
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
    </>
  );
}
